import React, { useState, useEffect } from 'react';
import { adminFaqAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, Eye, EyeOff, GripVertical, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminFaqsPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editFaq, setEditFaq] = useState(null);
  const [form, setForm] = useState({ question: '', answer: '', active: true });

  const fetchFaqs = async () => {
    try {
      const res = await adminFaqAPI.getAll();
      setFaqs(res.data.faqs);
    } catch { toast.error('Failed to load FAQs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFaqs(); }, []);

  const openCreate = () => {
    setEditFaq(null);
    setForm({ question: '', answer: '', active: true });
    setShowEditor(true);
  };

  const openEdit = (faq) => {
    setEditFaq(faq);
    setForm({ question: faq.question, answer: faq.answer, active: faq.active });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error('Question and answer are required'); return; }
    try {
      if (editFaq) {
        await adminFaqAPI.update(editFaq.id, form);
        toast.success('FAQ updated');
      } else {
        await adminFaqAPI.create(form);
        toast.success('FAQ created');
      }
      setShowEditor(false);
      fetchFaqs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save FAQ'); }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Delete this FAQ?`)) return;
    try {
      await adminFaqAPI.delete(faq.id);
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch { toast.error('Failed to delete FAQ'); }
  };

  const handleToggle = async (faq) => {
    try {
      const res = await adminFaqAPI.toggle(faq.id);
      toast.success(res.data.active ? 'FAQ activated' : 'FAQ deactivated');
      fetchFaqs();
    } catch { toast.error('Failed to toggle FAQ'); }
  };

  if (loading) return <div className="p-8 text-center"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>;

  if (showEditor) return (
    <div className="p-6 space-y-6" data-testid="faq-editor">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">{editFaq ? 'Edit FAQ' : 'Add New FAQ'}</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowEditor(false)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-faq-btn">Save FAQ</button>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
          <input value={form.question} onChange={e => setForm({...form, question: e.target.value})} className="w-full border rounded-lg px-3 py-2.5 text-sm" placeholder="e.g. How does GMB Autopilot work?" data-testid="faq-question-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
          <textarea value={form.answer} onChange={e => setForm({...form, answer: e.target.value})} rows={6} className="w-full border rounded-lg px-3 py-2.5 text-sm" placeholder="Provide a clear, helpful answer..." data-testid="faq-answer-input" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="admin-faqs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Manage FAQs</h1>
          <p className="text-gray-500 text-sm mt-1">Add, edit, and manage frequently asked questions shown on the homepage</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="add-faq-btn">
          <Plus className="w-4 h-4" /> Add FAQ
        </button>
      </div>
      <div className="space-y-3">
        {faqs.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center text-gray-500">
            <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No FAQs yet. Add your first FAQ.</p>
          </div>
        ) : faqs.map((faq, index) => (
          <div key={faq.id} className="bg-white rounded-xl border p-5 hover:shadow-sm transition-shadow" data-testid={`faq-item-${index}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400 font-mono">#{index + 1}</span>
                  <button onClick={() => handleToggle(faq)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${faq.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {faq.active ? <><Eye className="w-3 h-3" /> Active</> : <><EyeOff className="w-3 h-3" /> Inactive</>}
                  </button>
                </div>
                <h3 className="font-medium text-gray-900">{faq.question}</h3>
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{faq.answer}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(faq)} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" title="Edit" data-testid={`edit-faq-${index}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(faq)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50" title="Delete" data-testid={`delete-faq-${index}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminFaqsPage;
